import { ListboxInput, ListboxArrow, ListboxPopover, ListboxButton } from '@reach/listbox';
import React__default, { useRef, useCallback } from 'react';
import DayPicker from 'react-day-picker';
import styled, { css } from 'styled-components';
import { i18nPickerUtils } from '../../lib/date.js';
import { setTestId } from '../../lib/testId.js';
import { useTranslate, useLocale } from '../../lib/translate.js';
import { Button } from '../button/Button.js';
import { ButtonIcon } from '../button/ButtonIcon.js';
import { Chip } from '../chip/Chip.js';
import { COLOR_PALETTE } from '../colors/colorPalette.js';
import { FIELD_ERROR_DATA_ATTRIBUTE, setErrorDataAttribute } from '../fields/FieldError.js';
import { Icon } from '../icon/Icon.js';
import { Inline } from '../layout/Inline.js';
import { Stack } from '../layout/Stack.js';
import { useCheckModal } from '../modal/Modal.js';
import { CSSGenerateTextFieldError } from '../styles/cssGenerateField.js';
import { getColor } from '../styles/stylePropTypes.js';
import { Text } from '../text/Text.js';
import { CSSDefaultInput, INPUT_DISABLED_DATA_ATTRIBUTE, CSSDisabledInput, INPUT_READ_ONLY_DATA_ATTRIBUTE, CSSReadOnlyInput, CSSDefaultPopover, setDisabledDataAttribute, setReadOnlyDataAttribute, InnerInputLabel } from './InputCommon.js';

const DateInputWrapper = styled(Stack)`
  /* override the 'standard' date picker styles */
  /* (we don't completely re-write the styles, as then we would have to handle open
  /* states .etc) */
  & .DayPickerInput-Overlay {
    background-color: white;
    box-shadow: rgba(0, 0, 0, 0.16) 0px 2px 3px 0px;
    border: 1px solid ${COLOR_PALETTE.slateGrey40};
    border-radius: 0.25rem;
    padding: 0;
  }

  & *:focus {
    outline: none;
  }

  & .DayPicker-Day,
  & .DayPicker-WeekDay {
    font-size: 0.75rem;
    font-weight: 500;
    height: 2rem;
    width: 2rem;
    min-width: 2rem;
  }

  &
    .DayPicker:not(.DayPicker--interactionDisabled)
    .DayPicker-Day:not(.DayPicker-Day--disabled):not(.DayPicker-Day--selected):not(.DayPicker-Day--outside):hover,
  &
    .DayPicker:not(.DayPicker--interactionDisabled)
    .DayPicker-Day:not(.DayPicker-Day--disabled):not(.DayPicker-Day--selected):not(.DayPicker-Day--outside):focus {
    background-color: ${(props)=>getColor("context10", props.theme)};
  }

  & .DayPicker-Day--selected:not(.DayPicker-Day--disabled):not(.DayPicker-Day--outside) {
    color: ${COLOR_PALETTE.white};
    background-color: ${(props)=>getColor("context", props.theme)};
  }

  & .DayPicker-Day--today:not(.DayPicker-Day--disabled) {
    color: ${(props)=>getColor("context", props.theme)};
  }

  & .DayPicker-Caption {
    font-size: 1rem;
    font-weight: 600;
    text-align: center;
  }

  & .DayPicker-NavButton {
    font-size: 2.5rem;
    top: 0;
    margin: 0;
  }

  /* manually override the date picker 'next/previous' button icons */
  & .DayPicker-NavButton.DayPicker-NavButton--prev {
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' focusable='false' style='height:1em; width:1em;' fill='%23425563' viewBox='0 0 24 24' aria-hidden='true' role='presentation'%3E%3Cpath d='M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z'%3E%3C/path%3E%3Cpath fill='none' d='M0 0h24v24H0V0z'%3E%3C/path%3E%3C/svg%3E");
    left: 0;
  }
  & .DayPicker-NavButton.DayPicker-NavButton--next {
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' focusable='false' style='height:1em; width:1em;' fill='%23425563' viewBox='0 0 24 24' aria-hidden='true' role='presentation'%3E%3Cpath d='M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z'%3E%3C/path%3E%3Cpath fill='none' d='M0 0h24v24H0V0z'%3E%3C/path%3E%3C/svg%3E");
    right: 0;
  }
`;
const StyledListboxInput = styled(ListboxInput)`
  width: 100%;

  &[data-hpy-web-date-input][data-hpy-web-date-input-base='true'] [data-reach-listbox-button] {
    ${CSSDefaultInput}
    position: relative;
    display: flex;
    cursor: pointer;
    padding: 1.5rem 2.5rem 0.5rem 1rem;
    align-items: flex-start;
    min-width: 13rem;

    &[data-hpy-web-defaultinput-input-has-icon='true'] [data-hpy-web-defaultinput-input-inner-label] {
      padding-left: 2rem;
    }

    &[data-hpy-web-defaultinput-input-has-icon='true'][data-hpy-web-defaultinput-input-has-value='true']
      [data-hpy-web-defaultinput-input-inner-label] {
      padding-left: 2.625rem;
    }

    &[${FIELD_ERROR_DATA_ATTRIBUTE}],
    &[${FIELD_ERROR_DATA_ATTRIBUTE}]:hover,
    &[${FIELD_ERROR_DATA_ATTRIBUTE}]:focus {
      ${CSSGenerateTextFieldError()}
      color: inherit;
    }

    &[${INPUT_DISABLED_DATA_ATTRIBUTE}],
    &[${INPUT_DISABLED_DATA_ATTRIBUTE}] [data-hpy-web-text-input-inner-label],
    &[${INPUT_DISABLED_DATA_ATTRIBUTE}] [data-hpy-web-text-input-icon] {
      ${CSSDisabledInput}
    }

    &[${INPUT_READ_ONLY_DATA_ATTRIBUTE}] {
      ${CSSReadOnlyInput}
    }
  }

  &[data-hpy-web-date-input-base='true'][data-state='expanded'] [data-reach-listbox-button],
  &[data-hpy-web-date-input-base='true'][data-state='expanded'] [data-reach-listbox-button]:hover,
  &[data-hpy-web-date-input-base='true'] [data-reach-listbox-button]:focus {
    outline: none;
    border-color: ${(props)=>getColor("context", props.theme)};
  }
`;
const StyledListBoxArrow = styled(ListboxArrow)`
  position: absolute;
  top: 50%;
  margin-top: -2px;
  right: 1rem;
  display: block;
  width: 0px;
  height: 0px;
  content: '';
  transform: rotate(0deg);
  border-top: 5px solid ${COLOR_PALETTE.slateGrey100};
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  transition: transform 0.2s ease 0s, -webkit-transform 0.2s ease 0s;

  &[data-expanded] {
    transform: rotate(180deg);
  }
`;
const StyledListboxPopover = styled(ListboxPopover)`
  &[data-reach-listbox-popover] {
    ${CSSDefaultPopover}
    max-width: fit-content;
    min-width: 16rem;
    /* set fix height on calendar popover
    to avoid the calendar resizing */
    height: 17.5rem;
  }
`;
const StyledText = styled(Text)`
  ${(props)=>props.$highlight && css`
      color: ${COLOR_PALETTE.red100};
    `};
`;
/**
 * Date Input with Formik Field bindings.
 *
 * @example
 * <DateInput name="scheduled" />
 */ const DateInputAs = ({ label , sublabel , value , disabled =false , disabledDays , format ="system:Date.relative" , readOnly =false , hasError =false , highlight =false , onChange , onBlur , testId ="X" , canDeselectDay , // eslint-disable-next-line @typescript-eslint/no-unused-vars
children , ...props })=>{
    const isModal = useCheckModal();
    const ref = useRef();
    const onDayClick = useCallback((v, { selected , disabled  })=>{
        if (onChange === undefined || disabled) {
            return;
        }
        if (selected && canDeselectDay) {
            onChange(undefined);
        } else {
            onChange(v);
        }
        // Giving the button focus hides the select dropdown
        ref?.current?.focus();
    }, [
        onChange
    ]);
    const onListBoxBlur = useCallback(()=>{
        if (onBlur !== undefined) {
            onBlur(value);
        }
    }, [
        onBlur,
        value
    ]);
    const t = useTranslate();
    const locale = useLocale();
    let asProps = {};
    let asComp = undefined;
    if (isButton(props)) {
        asProps = {
            testId,
            icon: props.icon !== undefined ? props.icon ?? "calendar" : null,
            variant: props.variant,
            size: props.size
        };
        asComp = Button;
    }
    if (isButtonIcon(props)) {
        asProps = {
            testId,
            icon: value ? null : props.icon ?? "calendar",
            variant: props.variant,
            size: props.size
        };
        asComp = ButtonIcon;
    }
    if (isChip(props)) {
        asProps = {
            testId,
            label,
            highlight: highlight,
            sublabel: !!value ? t(format, {
                date: value
            }) : sublabel,
            icon: props.icon ?? "calendar"
        };
        asComp = Chip;
    }
    return React__default.createElement(StyledListboxInput, {
        "data-hpy-web-date-input": true,
        ...isBase(props) ? {
            "data-hpy-web-date-input-base": true
        } : {},
        ...setTestId("DateInput-" + testId)
    }, React__default.createElement(ListboxButton, {
        "data-hpy-web-defaultinput-input": true,
        "data-hpy-web-defaultinput-input-has-icon": true,
        "data-hpy-web-defaultinput-input-has-value": value !== undefined,
        ...setDisabledDataAttribute(disabled),
        ...setReadOnlyDataAttribute(readOnly),
        ...setErrorDataAttribute(hasError),
        ...setTestId("Button-DateInput-" + testId),
        ...asProps,
        ref: ref,
        as: asComp,
        disabled: disabled || readOnly,
        onBlur: onListBoxBlur
    }, children, isBase(props) && React__default.createElement(React__default.Fragment, null, React__default.createElement(Icon, {
        "data-hpy-web-defaultinput-input-icon": true,
        icon: "calendar"
    }), React__default.createElement(StyledText, {
        "data-hpy-web-defaultinput-input-label": true,
        "$highlight": highlight
    }, t(format, {
        date: value
    })), !readOnly && !disabled && React__default.createElement(StyledListBoxArrow, null, "\xa0"), React__default.createElement(InnerInputLabel, {
        "data-hpy-web-defaultinput-input-inner-label": true,
        size: "small"
    }, label)), isButton(props) && React__default.createElement(Inline, {
        alignY: "center"
    }, React__default.createElement(Text, {
        size: "small"
    }, label))), React__default.createElement(StyledListboxPopover, {
        portal: !isModal,
        ...setTestId("DateInput-Popover-" + testId)
    }, React__default.createElement(DateInputWrapper, {
        ...setErrorDataAttribute(hasError)
    }, React__default.createElement(DayPicker, {
        month: value,
        disabledDays: disabledDays,
        selectedDays: value,
        locale: locale,
        localeUtils: i18nPickerUtils,
        onDayClick: onDayClick
    }))));
};
DateInputAs.dataId = "data-hpy-web-date-input";
DateInputAs.displayName = "DateInputAs";
const isBase = (props)=>props?.as === undefined;
const isButton = (props)=>props?.as === "button";
const isButtonIcon = (props)=>props?.as === "buttonIcon";
const isChip = (props)=>props?.as === "chip";
/**
 * Date Input.
 *
 * @example
 * <DateInput name="scheduled" />
 */ const DateInput = ({ children , ...props })=>{
    if (isBase(props)) {
        return React__default.createElement(DateInputAs, {
            ...props
        }, children);
    }
    if (isButton(props)) {
        return React__default.createElement(DateInputAs, {
            ...props
        }, children);
    }
    if (isButtonIcon(props)) {
        return React__default.createElement(DateInputAs, {
            ...props
        }, children);
    }
    if (isChip(props)) {
        return React__default.createElement(DateInputAs, {
            ...props
        }, children);
    }
    return null;
};
DateInput.dataId = "data-hpy-web-date-input";
DateInput.displayName = "DateInput";

export { DateInput };
